import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Loading from '@jg/loading';
import { useTranslation } from 'react-i18next';
import { withAuth } from '@jg/auth-react';
import { PageView, Button } from '@jg/react-tracking';
import PageCard from '@jg/page-card';
import { Helmet } from 'react-helmet';
import { RouteComponentProps, RouteProps, withRouter } from 'react-router';
import { Auth } from '@jg/auth';

import { REQUEST } from '../../consts';
import { getFundraisingPagesData } from '../../selectors/apiSelectors';
import {
  getRequestInFlight,
  getRequestError,
} from '../../selectors/httpSelectors';
import { appActions, apiThunk, httpActions, appThunk } from '../../actions';
import { getSelectedFundraisingPage } from '../../selectors/appSelectors';
import { getJoinRoutes, JoinRoutes } from '../../selectors/routeSelectors';
import CardWithRadioWrapper from '../../components/CardWithRadioWrapper/CardWithRadioWrapper';
import FatalError from '../../components/FatalError/FatalError';
import Divider from '../../components/Divider/Divider';
import { makePhotoUrl } from '../../utils';
import getFRPDataByGuid from '../../utils/getFRPDataByGuid';

import {
  IAppState,
  IFundraisingTeamData,
  IFundraisingPagesData,
} from '../../types/state.d';

type LinkFundraisingPageDispatchToProps = {
  selectFundraisingPage: typeof appActions.selectFundraisingPage;
  linkSelectedFundraisingPage: typeof apiThunk.linkSelectedFundraisingPage;
  pushToPage: typeof appThunk.pushToPage;
  resetRequestError: typeof httpActions.resetRequestError;
};
type LinkFundraisingPageMapStateToProps = {
  fundraisingPages: IFundraisingPagesData;
  isInFlight?: boolean;
  isError: boolean;
  isFetchFrpDataFailed: boolean;
  selectedFundraisingPage: IAppState['app']['selectedFundraisingPage'];
  joinRoutes: JoinRoutes;
};

type LinkFundraisingPageProps = RouteComponentProps<unknown> &
  LinkFundraisingPageDispatchToProps &
  LinkFundraisingPageMapStateToProps & {
    auth: Auth;
  };

export const LinkFundraisingPage = ({
  linkSelectedFundraisingPage,
  auth,
  fundraisingPages,
  selectedFundraisingPage,
  selectFundraisingPage,
  isError,
  resetRequestError,
  isFetchFrpDataFailed,
  isInFlight,
  pushToPage,
  joinRoutes,
}: LinkFundraisingPageProps) => {
  const { t } = useTranslation();
  const [charityName, setCharityName] = useState('');
  const displayLoading = isInFlight || isFetchFrpDataFailed;
  const hasFundraisingPages = fundraisingPages.length > 0;
  const hasOneEligiblePage = fundraisingPages.length === 1;

  useEffect(
    () => {
      if (hasOneEligiblePage) {
        getFRPDataByGuid(fundraisingPages[0].fundraiserPageGuid).then(json => {
          if (json) {
            setCharityName(json.charities[0].charityName);
          }
        });
      }
    },
    [fundraisingPages],
  );

  const renderSingleFundraisingPage = () => {
    const accessToken = auth.getAccessToken();
    const fundraisingPage = fundraisingPages[0];
    const myProfile = [
      {
        picture: makePhotoUrl(accessToken!.profilePictureUrl),
        id: accessToken!.guid,
      },
    ];
    return (
      <PageCard
        coverPhoto={makePhotoUrl(fundraisingPage.heroImageName)}
        pageName={fundraisingPage.pageTitle}
        charityName={charityName}
        usersList={myProfile}
        pageOwner={`${accessToken!.givenName} ${accessToken!.familyName}`}
      />
    );
  };

  const renderMultipleFundraisingPages = () => (
    <ul className="jg-list--unstyled jg-space-mbn">
      {fundraisingPages.map(fundraisingPage => (
        <li className="jg-space-mtsm" key={fundraisingPage.fundraiserPageGuid}>
          <CardWithRadioWrapper<IFundraisingTeamData['fundraiserPageGuid']>
            id={`fundraisingPage-${fundraisingPage.fundraiserPageGuid}`}
            name="fundraisingPage"
            photo={makePhotoUrl(fundraisingPage.heroImageName)}
            text={fundraisingPage.pageTitle}
            onChangeFunc={selectFundraisingPage}
            checked={
              selectedFundraisingPage === fundraisingPage.fundraiserPageGuid
            }
            value={fundraisingPage.fundraiserPageGuid}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <PageView
      trackingData={{
        subtype: 'join team',
        event_value: 'link your page',
        page_section: 'join team > link your page',
      }}
    >
      <Helmet title={t('title.join.linkYourFRP')} />
      <main
        className="jg-space-ptmd jg-space-pbxl jg-space-pbml@md"
        data-qa="connect-frp-page"
      >
        <div className="jg-text--center jg-space-mbmd">
          <h1 className="jg-space-mbxs">
            {t('content.join.linkfundraisingpage.heading')}
          </h1>
          <p data-qa="link-your-page-subtext" className="jg-text--large">
            {t(
              `content.join.linkfundraisingpage.${
                hasOneEligiblePage ? 'onefrpsubtext' : 'multiplefrpsubtext'
              }`,
            )}
          </p>
        </div>
        {hasOneEligiblePage
          ? renderSingleFundraisingPage()
          : renderMultipleFundraisingPages()}
        <div className="row">
          <div className="col-md-8 offset-md-2">
            {!displayLoading &&
              hasFundraisingPages && (
                <>
                  <Button
                    data-qa="link-existing-button"
                    onClick={linkSelectedFundraisingPage}
                    type="button"
                    className="jg-btn jg-space-mtlg"
                    trackingData={{
                      action_name: 'click',
                      subtype: 'button',
                      event: 'click',
                      event_value: 'link page',
                      page_section: 'join team > link your page',
                    }}
                  >
                    {t('content.join.linkfundraisingpage.button.linkexisting')}
                  </Button>
                  <Divider className="jg-space-mvmd jg-space-mhn">
                    {t('global.or')}
                  </Divider>
                </>
              )}
            <Button
              data-qa="create-fundraising-button"
              type="button"
              disabled={displayLoading}
              onClick={() =>
                joinRoutes.nextStep && pushToPage(joinRoutes.nextStep)
              }
              className="jg-btn"
              trackingData={{
                action_name: 'click',
                subtype: 'button',
                event: 'click',
                event_value: 'create fundraising page',
                page_section: 'join team > link your page',
              }}
            >
              {displayLoading ? (
                <>
                  <span className="jg-hidden-visually">
                    {t('global.loading')}
                  </span>

                  <Loading size="small" colour="white" />
                </>
              ) : (
                t('content.join.button.createfundraisingpage')
              )}
            </Button>
          </div>
        </div>
        {isError && <FatalError closeButtonClick={resetRequestError} />}
        {isFetchFrpDataFailed && (
          <FatalError refresh closeButtonClick={resetRequestError} />
        )}
      </main>
    </PageView>
  );
};

export const mapDispatchToProps: LinkFundraisingPageDispatchToProps = {
  selectFundraisingPage: appActions.selectFundraisingPage,
  linkSelectedFundraisingPage: apiThunk.linkSelectedFundraisingPage,
  pushToPage: appThunk.pushToPage,
  resetRequestError: httpActions.resetRequestError,
};

export const mapStateToProps = (
  state: IAppState,
  ownProps: RouteProps,
): LinkFundraisingPageMapStateToProps => ({
  isInFlight: getRequestInFlight(REQUEST.GET_FUNDRAISING_PAGES)(state),
  isFetchFrpDataFailed: getRequestError(REQUEST.GET_FUNDRAISING_PAGES)(state),
  isError: getRequestError(REQUEST.LINK_FUNDRAISING_PAGE)(state),
  fundraisingPages: getFundraisingPagesData(state),
  selectedFundraisingPage: getSelectedFundraisingPage(state),
  joinRoutes: getJoinRoutes(state, ownProps),
});

export default withRouter(
  withAuth(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(LinkFundraisingPage),
  ),
);
